"use client"

import { createContext, useContext, ReactNode } from "react";
import { useEffect, useCallback, useState } from "react";

export type ScreenSize = "DESKTOP" | "TABLET" | "MOBILE";

interface ScreenSizeContextType {
    screenSize: ScreenSize;
}

export const ScreenSizeContext = createContext<ScreenSizeContextType | undefined>(undefined);

export const useScreenSize = (): ScreenSize => {
    const context = useContext(ScreenSizeContext);
    if (!context) {
        throw new Error("useScreenSize must be used within a ScreenSizeProvider");
    }
    return context.screenSize;
};

interface ScreenSizeProviderProps {
    children: ReactNode;
}

export const ScreenSizeProvider: React.FC<ScreenSizeProviderProps> = ({ children }) => {
    const [screenSize, setScreenSize] = useState<ScreenSize>("DESKTOP");

    const handleResize = useCallback(() => {
        const width = window.innerWidth;
        if (width < 1024) {
            setScreenSize("MOBILE");
        } else if (width < 1200) {
            setScreenSize("TABLET");
        } else {
            setScreenSize("DESKTOP");
        }
    }, []);

    useEffect(() => {
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [handleResize]);

    return (
        <ScreenSizeContext.Provider value={{ screenSize }}>
            {children}
        </ScreenSizeContext.Provider>
    );
};